/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import './rendered_data_panel.less';
import 'neuroglancer/noselect.css';

import {Annotation, makeSingleLineAnnotation} from 'neuroglancer/annotation';
import {getAnnotationTypeRenderHandler} from 'neuroglancer/annotation/type_handler';
import {DisplayContext, RenderedPanel} from 'neuroglancer/display_context';
import {NavigationState} from 'neuroglancer/navigation_state';
import {PickIDManager} from 'neuroglancer/object_picking';
import {displayToLayerCoordinates, layerToDisplayCoordinates} from 'neuroglancer/render_coordinate_transform';
import {AutomaticallyFocusedElement} from 'neuroglancer/util/automatic_focus';
import {Borrowed} from 'neuroglancer/util/disposable';
import {ActionEvent, EventActionMap, registerActionListener} from 'neuroglancer/util/event_action_map';
import {AXES_NAMES, kAxes, mat4, vec2, vec3, vec4} from 'neuroglancer/util/geom';
import {KeyboardEventBinder} from 'neuroglancer/util/keyboard_bindings';
import * as matrix from 'neuroglancer/util/matrix';
import {MouseEventBinder} from 'neuroglancer/util/mouse_bindings';
import {startRelativeMouseDrag} from 'neuroglancer/util/mouse_drag';
import {TouchEventBinder, TouchPinchInfo, TouchTranslateInfo} from 'neuroglancer/util/touch_bindings';
import {getWheelZoomAmount} from 'neuroglancer/util/wheel_zoom';
import {ViewerState} from 'neuroglancer/viewer_state';
import { changeBinSize } from './viewer';
import { allToolsButton, viewer as _layoutviewer, local_host_SN_flag } from 'src/main';
import { debounce } from 'lodash';
// import { ImageUserLayer } from './image_user_layer';
import { LoadedDataSubsource, LoadedLayerDataSource } from './layer_data_source';
// import { packColor } from './util/color';
import { ImageUserLayer } from './image_user_layer';
import { packColor } from './util/color';
import { UserLayer } from './layer';
import { makeElement } from './ui/layer_gene_table_tab';
import { ConfigIndependDialog } from './ui/congfig-dialog';
import { message } from './widget/message';
import { cancellableFetchSpecialOk } from './util/special_protocol_request';
import { responseJson } from './util/http_request';
import i18n from './i18n/i18n';
import { thousandth_formatter } from './util/comm_fun_hq';
declare var NEUROGLANCER_SHOW_OBJECT_SELECTION_TOOLTIP: boolean|undefined;

const tempVec3 = vec3.create();
let onKeyDow: boolean = false;

export interface RenderedDataViewerState extends ViewerState {
  inputEventMap: EventActionMap;
}

export class FramePickingData {
  pickIDs = new PickIDManager();
  viewportWidth: number = 0;
  viewportHeight: number = 0;
  invTransform = mat4.create();
  frameNumber: number = -1;
}

export class PickRequest {
  buffer: WebGLBuffer|null = null;
  glWindowX: number = 0;
  glWindowY: number = 0;
  frameNumber: number;
  sync: WebGLSync|null;
}

const pickRequestInterval = 30;

export const pickRadius = 5;
export const pickDiameter = 1 + pickRadius * 2;

/**
 * Sequence of offsets into C order (pickDiamater, pickDiamater) array in order of increasing
 * distance from center.
 */
export const pickOffsetSequence = (() => {
  const maxDist2 = pickRadius ** 2;
  const getDist2 = (x: number, y: number) => (x - pickRadius) ** 2 + (y - pickRadius) ** 2;

  let offsets = new Uint32Array(pickDiameter * pickDiameter);
  let count = 0;
  for (let x = 0; x < pickDiameter; ++x) {
    for (let y = 0; y < pickDiameter; ++y) {
      if (getDist2(x, y) > maxDist2) continue;
      offsets[count++] = y * pickDiameter + x;
    }
  }
  offsets = offsets.subarray(0, count);
  offsets.sort((a, b) => {
    const x1 = a % pickDiameter;
    const y1 = (a - x1) / pickDiameter;
    const x2 = b % pickDiameter;
    const y2 = (b - x2) / pickDiameter;
    return getDist2(x1, y1) - getDist2(x2, y2);
  });
  return offsets;
})();

/**
 * Sets array elements to 0 that would be outside the viewport.
 *
 * @param buffer Array view, which contains a C order (pickDiameter, pickDiameter) array.
 * @param baseOffset Offset into `buffer` corresponding to (0, 0).
 * @param stride Stride between consecutive elements of the array.
 * @param glWindowX Center x position, must be integer.
 * @param glWindowY Center y position, must be integer.
 * @param viewportWidth Width of viewport in pixels.
 * @param viewportHeight Width of viewport in pixels.
 */
export function clearOutOfBoundsPickData(
    buffer: Float32Array, baseOffset: number, stride: number, glWindowX: number, glWindowY: number,
    viewportWidth: number, viewportHeight: number) {
  const startX = glWindowX - pickRadius;
  const startY = glWindowY - pickRadius;
  if (startX >= 0 && startY >= 0 && startX + pickDiameter <= viewportWidth &&
      startY + pickDiameter <= viewportHeight) {
    return;
  }
  for (let relativeY = 0; relativeY < pickDiameter; ++relativeY) {
    for (let relativeX = 0; relativeX < pickDiameter; ++relativeX) {
      const x = startX + relativeX;
      const y = startY + relativeY;
      if (x < 0 || y < 0 || x >= viewportWidth || y >= viewportHeight) {
        buffer[baseOffset + (y * pickDiameter + x) * stride] = 0;
      }
    }
  }
}

const inputEventMap = EventActionMap.fromObject({
  'arrowup': {action: 'move-up'},
  'arrowdown': {action: 'move-down'},
  'arrowleft': {action: 'move-left', preventDefault: false},
  'arrowright': {action: 'move-right', preventDefault: false},
});

export abstract class RenderedDataPanel extends RenderedPanel {
  /**
   * Current mouse position within the viewport, or -1 if the mouse is not in the viewport.
   */
  mouseX = -1;
  mouseY = -1;

  /**
   * If `false`, either the mouse is not within the viewport, or a picking request was already
   * issued for the current mouseX and mouseY after the most recent frame was rendered; when the
   * current pick requests complete, no additional pick requests will be issued.
   *
   * If `true`, a picking request was not issued for the current mouseX and mouseY due to all pick
   * buffers being in use; when a pick buffer becomes available, an additional pick request will be
   * issued.
   */
  pickRequestPending = false;

  private mouseStateForcer = () => this.blockOnPickRequest();

  inputEventMap: EventActionMap;

  abstract navigationState: NavigationState;

  pickingData = [new FramePickingData(), new FramePickingData()];
  pickRequests = [new PickRequest(), new PickRequest()];
  pickBufferContents: Float32Array = new Float32Array(2 * 4 * pickDiameter * pickDiameter);

  /**
   * Reads pick data for the current mouse position into the currently-bound pixel pack buffer.
   */
  abstract issuePickRequest(glWindowX: number, glWindowY: number): void;

  /**
   * Timer id for checking if outstanding pick requests have completed.
   */
  private pickTimerId = -1;

  private cancelPickRequests() {
    const {gl} = this;
    for (const request of this.pickRequests) {
      const {sync} = request;
      if (sync !== null) {
        gl.deleteSync(sync);
      }
      request.sync = null;
    }
    clearTimeout(this.pickTimerId);
    this.pickTimerId = -1;
  }

  private issuePickRequestInternal(pickRequest: PickRequest) {
    const {gl} = this;
    let {buffer} = pickRequest;
    if (buffer === null) {
      buffer = pickRequest.buffer = gl.createBuffer();
      gl.bindBuffer(WebGL2RenderingContext.PIXEL_PACK_BUFFER, buffer);
      gl.bufferData(
          WebGL2RenderingContext.PIXEL_PACK_BUFFER, 2 * 4 * 4 * pickDiameter * pickDiameter,
          WebGL2RenderingContext.STREAM_READ);
    } else {
      gl.bindBuffer(WebGL2RenderingContext.PIXEL_PACK_BUFFER, buffer);
    }
    const {renderViewport} = this;
    let glWindowX = this.mouseX - renderViewport.visibleLeftFraction * renderViewport.logicalWidth;
    let glWindowY = renderViewport.height -
        (this.mouseY - renderViewport.visibleTopFraction * renderViewport.logicalHeight);
    this.issuePickRequest(glWindowX, glWindowY);
    pickRequest.sync = gl.fenceSync(WebGL2RenderingContext.SYNC_GPU_COMMANDS_COMPLETE, 0);
    pickRequest.frameNumber = this.context.frameNumber;
    pickRequest.glWindowX = glWindowX;
    pickRequest.glWindowY = glWindowY;
    gl.flush();
    // TODO(jbms): maybe call gl.flush to ensure fence is submitted
    gl.bindBuffer(WebGL2RenderingContext.PIXEL_PACK_BUFFER, null);
    if (this.pickTimerId === -1) {
      this.scheduleCheckForPickRequestCompletion();
    }
    this.pickRequestPending = false;
    const {pickRequests} = this;
    if (pickRequest !== pickRequests[0]) {
      pickRequests[1] = pickRequests[0];
      pickRequests[0] = pickRequest;
    }
    this.nextPickRequestTime = Date.now() + pickRequestInterval;
  }

  abstract completePickRequest(
      glWindowX: number, glWindowY: number, data: Float32Array,
      pickingData: FramePickingData): void;

  completePickInternal(pickRequest: PickRequest) {
    const {gl} = this;
    const {pickBufferContents} = this;
    gl.bindBuffer(WebGL2RenderingContext.PIXEL_PACK_BUFFER, pickRequest.buffer);
    gl.getBufferSubData(WebGL2RenderingContext.PIXEL_PACK_BUFFER, 0, pickBufferContents);
    gl.bindBuffer(WebGL2RenderingContext.PIXEL_PACK_BUFFER, null);
    const {pickingData} = this;
    const {frameNumber} = pickRequest;
    this.completePickRequest(
        pickRequest.glWindowX, pickRequest.glWindowY, pickBufferContents,
        pickingData[0].frameNumber === frameNumber ? pickingData[0] : pickingData[1]);
  }

  private scheduleCheckForPickRequestCompletion() {
    this.pickTimerId = window.setTimeout(() => {
      this.pickTimerId = -1;
      this.checkForPickRequestCompletion();
    }, 0);
  }

  private checkForPickRequestCompletion(checkingBeforeDraw = false, block = false) {
    let currentFrameNumber = this.context.frameNumber;
    let cancelIfNotReadyFrameNumber = -1;
    if (checkingBeforeDraw) {
      --currentFrameNumber;
      cancelIfNotReadyFrameNumber = currentFrameNumber - 1;
    }
    const {pickRequests} = this;
    const {gl} = this;
    let remaining = false;
    let cancelRemaining = false;
    let available: PickRequest|undefined;
    for (const pickRequest of pickRequests) {
      const {sync} = pickRequest;
      if (sync === null) continue;
      const {frameNumber} = pickRequest;
      if (!cancelRemaining && frameNumber >= currentFrameNumber - 1) {
        if (block ||
            gl.getSyncParameter(sync, WebGL2RenderingContext.SYNC_STATUS) ===
                WebGL2RenderingContext.SIGNALED) {
          this.completePickInternal(pickRequest);
          cancelRemaining = true;
        } else if (frameNumber !== cancelIfNotReadyFrameNumber) {
          remaining = true;
          continue;
        }
      }
      gl.deleteSync(sync);
      pickRequest.sync = null;
      available = pickRequest;
    }
    const {pickTimerId} = this;
    if (remaining && pickTimerId === -1) {
      this.scheduleCheckForPickRequestCompletion();
    } else if (!remaining && pickTimerId !== -1) {
      window.clearTimeout(pickTimerId);
      this.pickTimerId = -1;
    }
    if (!checkingBeforeDraw && available !== undefined && this.pickRequestPending &&
        this.canIssuePickRequest()) {
      this.issuePickRequestInternal(available);
    }
  }

  private blockOnPickRequest() {
    if (this.pickRequestPending) {
      this.cancelPickRequests();
      this.nextPickRequestTime = 0;
      this.attemptToIssuePickRequest();
    }
    this.checkForPickRequestCompletion(/*checkingBeforeDraw=*/ false, /*block=*/ true);
  }

  draw() {
    const {width, height} = this.renderViewport;
    this.checkForPickRequestCompletion(true);
    const {pickingData} = this;
    pickingData[0] = pickingData[1];
    const currentFrameNumber = this.context.frameNumber;
    const newPickingData = pickingData[1];
    newPickingData.frameNumber = currentFrameNumber;
    newPickingData.viewportWidth = width;
    newPickingData.viewportHeight = height;
    newPickingData.pickIDs.clear();
    if (!this.drawWithPicking(newPickingData)) {
      newPickingData.frameNumber = -1;
      return;
    }
    // For the new frame, allow new pick requests regardless of interval since last request.
    this.nextPickRequestTime = 0;
    if (this.mouseX >= 0) {
      this.attemptToIssuePickRequest();
    }
  }

  abstract drawWithPicking(pickingData: FramePickingData): boolean;

  private nextPickRequestTime = 0;
  private pendingPickRequestTimerId = -1;

  private pendingPickRequestTimerExpired = () => {
    this.pendingPickRequestTimerId = -1;
    if (!this.pickRequestPending) return;
    this.attemptToIssuePickRequest();
  };

  private canIssuePickRequest(): boolean {
    const time = Date.now();
    const {nextPickRequestTime, pendingPickRequestTimerId} = this;
    if (time < nextPickRequestTime) {
      if (pendingPickRequestTimerId == -1) {
        this.pendingPickRequestTimerId =
            window.setTimeout(this.pendingPickRequestTimerExpired, nextPickRequestTime - time);
      }
      return false;
    }
    return true;
  }

  public attemptToIssuePickRequest() {
    if (!this.canIssuePickRequest()) return;
    const currentFrameNumber = this.context.frameNumber;
    const {gl} = this;

    const {pickRequests} = this;
    // Try to find an available PickRequest object.

    for (const pickRequest of pickRequests) {
      let {sync} = pickRequest;
      if (sync !== null) {
        if (pickRequest.frameNumber < currentFrameNumber - 1) {
          gl.deleteSync(sync);
        } else {
          continue;
        }
      }
      this.issuePickRequestInternal(pickRequest);
      return;
    }
  }

  /**
   * Called each time the mouse position relative to the top level of the rendered viewport changes.
   */
  private updateMousePosition(mouseX: number, mouseY: number): void {
    if (mouseX === this.mouseX && mouseY === this.mouseY) {
      return;
    }
    this.mouseX = mouseX;
    this.mouseY = mouseY;
    if (mouseX < 0) {
      // Mouse moved out of the viewport.
      this.pickRequestPending = false;
      this.cancelPickRequests();
      return;
    }
    const currentFrameNumber = this.context.frameNumber;
    const pickingData = this.pickingData[1];
    if (pickingData.frameNumber !== currentFrameNumber ||
        this.renderViewport.width !== pickingData.viewportWidth ||
        this.renderViewport.height !== pickingData.viewportHeight) {
      // Viewport size has changed since the last frame, which means a redraw is pending.  Don't
      // issue pick request now.  Once will be issued automatically after the redraw.
      return;
    }
    this.pickRequestPending = true;
    this.attemptToIssuePickRequest();
  }

  constructor(
      context: Borrowed<DisplayContext>, element: HTMLElement,
      public viewer: RenderedDataViewerState) {
    super(context, element, viewer.visibility);
    this.inputEventMap = viewer.inputEventMap;
    const keyboardHandler = this.registerDisposer(new KeyboardEventBinder(element, inputEventMap));
    keyboardHandler.allShortcutsAreGlobal = true;
    element.classList.add('neuroglancer-rendered-data-panel');
    element.classList.add('neuroglancer-panel');
    element.classList.add('neuroglancer-noselect');
    if (typeof NEUROGLANCER_SHOW_OBJECT_SELECTION_TOOLTIP !== 'undefined' &&
        NEUROGLANCER_SHOW_OBJECT_SELECTION_TOOLTIP === true) {
      element.title =
          'Double click to toggle display of object under mouse pointer.  Control+rightclick to pin/unpin selection.';
    }

    this.registerDisposer(new AutomaticallyFocusedElement(element));
    this.registerDisposer(new KeyboardEventBinder(element, this.inputEventMap));
    this.registerDisposer(new MouseEventBinder(element, this.inputEventMap, event => {
      this.onMousemove(event);
    }));
    this.registerDisposer(new TouchEventBinder(element, this.inputEventMap));

    this.registerEventListener(element, 'mousemove', this.onMousemove.bind(this));
    this.registerEventListener(element, 'touchstart', this.onTouchstart.bind(this));
    this.registerEventListener(element, 'mouseleave', () => this.onMouseout());
    this.registerEventListener(element, 'keydown',()=> onKeyDow = true);
    this.registerEventListener(element, 'keyup', ()=> onKeyDow = false);
    this.registerEventListener(element, 'mouseover', event => {
      if (event.target !== element) {
        this.onMouseout();
      }
    }, /*capture=*/ true);

    registerActionListener(element, 'select-position', () => {
      this.viewer.selectionDetailsState.select();
    });

    registerActionListener(element, 'snap', () => {
      this.navigationState.pose.snap();
    });

    registerActionListener(element, 'zoom-in', () => {
      this.navigationState.zoomBy(0.5);
    });

    registerActionListener(element, 'zoom-out', () => {
      this.navigationState.zoomBy(2.0);
    });

    registerActionListener(element, 'depth-range-decrease', () => {
      this.navigationState.depthRange.value *= 0.5;
    });

    registerActionListener(element, 'depth-range-increase', () => {
      this.navigationState.depthRange.value *= 2;
    });

    for (let axis = 0; axis < 3; ++axis) {
      let axisName = AXES_NAMES[axis];
      for (let sign of [-1, +1]) {
        let signStr = (sign < 0) ? '-' : '+';
        registerActionListener(element, `rotate-relative-${axisName}${signStr}`, () => {
          this.navigationState.pose.rotateRelative(kAxes[axis], sign * 0.1);
        });
        let tempOffset = vec3.create();
        registerActionListener(element, `${axisName}${signStr}`, () => {
          let {navigationState} = this;
          let offset = tempOffset;
          offset[0] = 0;
          offset[1] = 0;
          offset[2] = 0;
          offset[axis] = sign;
          navigationState.pose.translateVoxelsRelative(offset, true);
        });
      }
    }

    registerActionListener(element, 'zoom-via-wheel', (event: ActionEvent<WheelEvent>) => {
      const e = event.detail;
      this.onMousemove(e, false);
      this.zoomByMouse(getWheelZoomAmount(e));
    });

    registerActionListener(element, 'bin-via-wheel', debounce((event: ActionEvent<WheelEvent>) => {
      if(!onKeyDow) return;
      const curr_bin = _layoutviewer.binSize.value;
      let index = (allToolsButton.bin_list as string[]).indexOf(curr_bin as string);
      _layoutviewer.mouseState.active = false;
      const e = event.detail;
      if(e.deltaY > 0){
        // 向下
        index ++;
        if(index > (allToolsButton.bin_list as string[]).length - 1) return
      }else{
        index --;
        if(index < 0) return
      }
      _layoutviewer.clearActiveTool();
      _layoutviewer.configDialog?.close();
      changeBinSize((allToolsButton.bin_list as string[])[index]);
    },500));
    registerActionListener(element, 'go-back', debounce(() => {
      if(_layoutviewer.jsonCloneObj.length > 1){
        _layoutviewer.jsonCloneObj.pop();
        _layoutviewer.configStorage.clear();
        _layoutviewer.configDialog?.close();
        _layoutviewer.state.restoreState(JSON.parse(JSON.stringify(_layoutviewer.jsonCloneObj[_layoutviewer.jsonCloneObj.length - 1])) );
      } 
    },500));

    registerActionListener(
        element, 'adjust-depth-range-via-wheel', (event: ActionEvent<WheelEvent>) => {
          const e = event.detail;
          this.navigationState.depthRange.value *= getWheelZoomAmount(e);
        });

    // 屏蔽旋转拖拽组合
    registerActionListener(element, 'translate-via-mouse-drag', (e: ActionEvent<MouseEvent>) => {
      startRelativeMouseDrag(e.detail, (_event, deltaX, deltaY) => {
        this.translateByViewportPixels(deltaX, deltaY);
      });
    });

    registerActionListener(
        element, 'translate-in-plane-via-touchtranslate', (e: ActionEvent<TouchTranslateInfo>) => {
          const {detail} = e;
          this.translateByViewportPixels(detail.deltaX, detail.deltaY);
        });

    registerActionListener(
        element, 'translate-z-via-touchtranslate', (e: ActionEvent<TouchTranslateInfo>) => {
          const {detail} = e;
          let {navigationState} = this;
          let offset = tempVec3;
          offset[0] = 0;
          offset[1] = 0;
          offset[2] = detail.deltaY + detail.deltaX;
          navigationState.pose.translateVoxelsRelative(offset, true);
        });

    for (const amount of [1, 10]) {
      registerActionListener(element, `z+${amount}-via-wheel`, (event: ActionEvent<WheelEvent>) => {
        const e = event.detail;
        let {navigationState} = this;
        let offset = tempVec3;
        let delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
        offset[0] = 0;
        offset[1] = 0;
        offset[2] = (delta > 0 ? -1 : 1) * amount;
        navigationState.pose.translateVoxelsRelative(offset, true);
      });
    }


    registerActionListener(element, 'move-to-mouse-position', () => {
      // 配准时双击定位
      const isBin = _layoutviewer.layerManager.getLayerVisible('heatmap')
      if(document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true'){
        // 添加定位十字线
        // const layer = _layoutviewer.layerManager.getLayerByName('gene_heatmap_layer');
        // const annotationLayer = (layer?.layer as AnnotationUserLayer).annotationStates.states[0];
        // const source:any = (layer?.layer as UserLayer).dataSources[0].loadState as LoadedLayerDataSource;
        // console.log(viewer.selectedLayer.layer, viewer.mouseState.position, source);
        const select_layer = (document.getElementsByClassName('register-layer-select')[0] as HTMLSelectElement).value;
        if(!viewer.selectedLayer.layer)viewer.selectedLayer.layer = viewer.layerManager.getLayerByName(`transform_${select_layer}`)
        const source:LoadedDataSubsource = ((viewer.selectedLayer.layer?.layer as ImageUserLayer).dataSources[0].loadState as LoadedLayerDataSource).subsources[2];

        let mousePos = vec3.fromValues(viewer.mouseState.position[0],viewer.mouseState.position[1], 0);

        let originPointPosOnImage = _layoutviewer.unprojectMousePosToImage(mousePos);
        _layoutviewer.changeImageAdjustOrigin(originPointPosOnImage);

        const transform_o_x = originPointPosOnImage[0];
        const transform_o_y = originPointPosOnImage[1];
        const linex = makeSingleLineAnnotation(
          new Float32Array([transform_o_x - 5000, transform_o_y, 0]), 
          new Float32Array([transform_o_x + 5000, transform_o_y, 0]), 'line_x');
        linex.properties = [packColor(vec4.fromValues(1.0, 0.0, 0.0, 1.0))];
        // linex.properties = [0,0,1]
        const liney = makeSingleLineAnnotation(
          new Float32Array([transform_o_x, transform_o_y - 5000, 0]), 
          new Float32Array([transform_o_x, transform_o_y + 5000, 0]), 'line_y');
        liney.properties = [packColor(vec4.fromValues(0.0, 1.0, 0.0, 1.0))];
        // x 标注
        const arrowx1 = makeSingleLineAnnotation(
          new Float32Array([transform_o_x + 5400, transform_o_y + 200, 0]), 
          new Float32Array([transform_o_x + 5200, transform_o_y - 200, 0]), 'arrowx1');
        arrowx1.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
        const arrowx2 = makeSingleLineAnnotation(
          new Float32Array([transform_o_x + 5200, transform_o_y + 200, 0]), 
          new Float32Array([transform_o_x + 5400, transform_o_y - 200, 0]), 'arrowx2');
        arrowx2.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
        const arrowy1 = makeSingleLineAnnotation(
          new Float32Array([transform_o_x + 100, transform_o_y - 5600, 0]), 
          new Float32Array([transform_o_x, transform_o_y - 5400, 0]), 'arrowy1');
        arrowy1.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
        const arrowy2 = makeSingleLineAnnotation(
          new Float32Array([transform_o_x - 100, transform_o_y - 5600, 0]), 
          new Float32Array([transform_o_x, transform_o_y - 5400, 0]), 'arrowy2');
        arrowy2.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
        const arrowy3 = makeSingleLineAnnotation(
          new Float32Array([transform_o_x, transform_o_y - 5400, 0]), 
          new Float32Array([transform_o_x, transform_o_y - 5200, 0]), 'arrowy3');
        arrowy3.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
        if(source?.subsourceEntry?.subsource?.staticAnnotations?.references){
          for (let value_ssDNA of source?.subsourceEntry?.subsource?.staticAnnotations?.references) {
            const reference = source.subsourceEntry.subsource.staticAnnotations.getReference(value_ssDNA[0]);
            if(value_ssDNA[0] === 'line_x'){
              source.subsourceEntry.subsource.staticAnnotations?.update(reference, linex);
            }else if(value_ssDNA[0] === 'line_y'){
              source.subsourceEntry.subsource.staticAnnotations?.update(reference, liney);
            }else if(value_ssDNA[0] === 'arrowx1'){
              source.subsourceEntry.subsource.staticAnnotations?.update(reference, arrowx1);
            }else if(value_ssDNA[0] === 'arrowx2'){
              source.subsourceEntry.subsource.staticAnnotations?.update(reference, arrowx2);
            }else if(value_ssDNA[0] === 'arrowy1'){
              source.subsourceEntry.subsource.staticAnnotations?.update(reference, arrowy1);
            }else if(value_ssDNA[0] === 'arrowy2'){
              source.subsourceEntry.subsource.staticAnnotations?.update(reference, arrowy2);
            }else if(value_ssDNA[0] === 'arrowy3'){
              source.subsourceEntry.subsource.staticAnnotations?.update(reference, arrowy3);
            }
          }
        }
      }else if(Object.keys(_layoutviewer.saveSelectedKeyVal).length > 0 && isBin){
        const layer = _layoutviewer.layerManager.getLayerByName(allToolsButton.genelistFile, 'genename');
        if(layer){
          let state = layer.manager.layerSelectedValues.get(layer.layer as UserLayer);
          if (state !== undefined) {
            let nodeList = document.body.querySelectorAll(".more-details-configdialog") as NodeList;
            nodeList.forEach((item:Node)=>document.body.removeChild(item));
            // if(document.getElementsByClassName('more-details-configdialog'))
            const {property} = state;
            if(property !== undefined && property.length && state.annotationId !== undefined){
              let propertyStr = '';
              property.forEach((item)=>{
                if(!item.name.includes('dnb') && !item.name.includes('cellType')){
                  if (allToolsButton.genelistName?.includes('metabon') && item.name === "midCount") {
                    propertyStr += `<div><span>${ i18n.intensity }：</span><span>${ thousandth_formatter(item.counts) }</span></div>`
                  } else {
                    propertyStr += `<div><span>${ i18n[item.name] }：</span><span>${ thousandth_formatter(item.counts) }</span></div>`
                  }
                }
              })
              let positionx = layer.manager.layerSelectedValues.mouseState.position[0];
              let positiony = layer.manager.layerSelectedValues.mouseState.position[1];
              if((layer.layer as UserLayer).dataSources[0].spec_.transform){
                const newTransform = mat4.fromValues(0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0);
                let layerTransform:any = [];
                (<any>viewer).layerManager.managedLayers.forEach((item: any, index: number)=>{
                  if(item.visible){
                    layerTransform = (<any>viewer).layerManager.managedLayers[index].layer_?.dataSources[0]?.loadState_!.transform!.value_.transform as any;
                    if(layerTransform.length < 16){
                      layerTransform = [layerTransform[0], layerTransform[1], layerTransform[2], 0,
                      layerTransform[3], layerTransform[4], layerTransform[5], 0,
                      layerTransform[6], layerTransform[7], layerTransform[8], 0,
                      0, 0, 0, 1]
                    }
                  }
                })
                mat4.invert(newTransform, layerTransform)
                let coor = vec4.fromValues(layer.manager.layerSelectedValues.mouseState.position[0], layer.manager.layerSelectedValues.mouseState.position[1], 1, 1);
                vec4.transformMat4(coor, coor, newTransform);
                positionx = Math.floor( coor[0] );
                positiony = Math.floor( coor[1] )
              }
              propertyStr = `<div><span>${i18n.location} </span><span>(${positionx}, ${positiony})</span></div>${propertyStr}`;
              const content = makeElement('div',['ng-details-of-more-gene'],{},`<div class="ng-details-of-more-gene-top"><p>${i18n.detail_of_bin} (${_layoutviewer.binSize.value})</p>
              <div class="ng-details-of-more-gene-top-inner">${propertyStr}</div></div>`)
              this.getElementGene(positionx, positiony, content);
              const dom = (document.getElementsByClassName('gene-view')[0]).getElementsByClassName('selecte-views-label')[0] as HTMLElement;
              new ConfigIndependDialog(dom,content, {position: 'bottom', unique: false, className: 'more-details-configdialog'});
            }
          }
        }
      }
    });

    registerActionListener(element, 'snap', () => this.navigationState.pose.snap());

    registerActionListener(element, 'move-annotation', (e: ActionEvent<MouseEvent>) => {
      const {mouseState} = this.viewer;
      const selectedAnnotationId = mouseState.pickedAnnotationId;
      const annotationLayer = mouseState.pickedAnnotationLayer;
      if (annotationLayer !== undefined) {
        if (selectedAnnotationId !== undefined) {
          e.stopPropagation();
          let annotationRef = annotationLayer.source.getReference(selectedAnnotationId)!;
          let ann = <Annotation>annotationRef.value;

          const handler = getAnnotationTypeRenderHandler(ann.type);
          const pickedOffset = mouseState.pickedOffset;
          const {chunkTransform: {value: chunkTransform}} = annotationLayer;
          if (chunkTransform.error !== undefined) return;
          const {layerRank} = chunkTransform;
          const repPoint = new Float32Array(layerRank);
          handler.getRepresentativePoint(repPoint, ann, mouseState.pickedOffset);
          let totDeltaVec = vec2.set(vec2.create(), 0, 0);
          if (mouseState.updateUnconditionally()) {
            startRelativeMouseDrag(
                e.detail,
                (_event, deltaX, deltaY) => {
                  vec2.add(totDeltaVec, totDeltaVec, [deltaX, deltaY]);
                  const layerPoint = new Float32Array(layerRank);
                  matrix.transformPoint(
                      layerPoint, chunkTransform.chunkToLayerTransform, layerRank + 1, repPoint,
                      layerRank);
                  const renderPt = tempVec3;
                  const {displayDimensionIndices} =
                      this.navigationState.pose.displayDimensions.value;
                  layerToDisplayCoordinates(
                      renderPt, layerPoint, chunkTransform.modelTransform, displayDimensionIndices);
                  this.translateDataPointByViewportPixels(
                      renderPt, renderPt, totDeltaVec[0], totDeltaVec[1]);
                  displayToLayerCoordinates(
                      layerPoint, renderPt, chunkTransform.modelTransform, displayDimensionIndices);
                  const newPoint = new Float32Array(layerRank);
                  matrix.transformPoint(
                      newPoint, chunkTransform.layerToChunkTransform, layerRank + 1, layerPoint,
                      layerRank);
                  let newAnnotation =
                      handler.updateViaRepresentativePoint(ann, newPoint, pickedOffset);
                  annotationLayer.source.update(annotationRef, newAnnotation);
                },
                (_event) => {
                  annotationLayer.source.commit(annotationRef);
                  annotationRef.dispose();
                });
          }
        }
      }
    });

    registerActionListener(element, 'delete-annotation', () => {
      const {mouseState} = this.viewer;
      const selectedAnnotationId = mouseState.pickedAnnotationId;
      const annotationLayer = mouseState.pickedAnnotationLayer;
      if (annotationLayer !== undefined && !annotationLayer.source.readonly &&
          selectedAnnotationId !== undefined) {
        const ref = annotationLayer.source.getReference(selectedAnnotationId);
        try {
          annotationLayer.source.delete(ref);
        } finally {
          ref.dispose();
        }
      }
    });

    registerActionListener(element, 'zoom-via-touchpinch', (e: ActionEvent<TouchPinchInfo>) => {
      const {detail} = e;
      this.handleMouseMove(detail.centerX, detail.centerY);
      const ratio = detail.prevDistance / detail.distance;
      if (ratio > 0.1 && ratio < 10) {
        this.zoomByMouse(ratio);
      }
    });
  }
  sortFieldMatch (field:string) {
    let fields = field.includes('ID') ? 'id': '';
    const stringArray = field.replace('ID','').split('') // 将字符串分割成相应的字符串数组
    let newField = field.replace('ID','');
    stringArray.forEach(t => {
      if (/[A-Z]/.test(t)) {
        // 遍历分割之后的字符串组，将找到的大写字母替换成其他字符串，将替换后的字符串赋值给另外一个新的string 变量
        newField = newField.replace(t, ` ${t.toLowerCase()}`)
      }
    })
    return newField + fields;
  }
  async getElementGene(x: number, y: number, element: HTMLElement){
    let genes = Object.keys(_layoutviewer.saveSelectedKeyVal).join(',');
    let flag:boolean = false;
    const response = await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/annotation/many_gene_each_element_mid/${genes}/${allToolsButton.genelistFile}/${_layoutviewer.binSize.value}/${x}/${y}`, {}, responseJson);
    if(response.code === 0){
      const title = makeElement('div',['ng-details-of-more-gene-info-title', 'details-of-more-gene-fold'],{},`
      <div>
        <div class="down-icon ng-details-of-more-gene-icon"><svg width="14px" height="10px" viewBox="0 0 14 10" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g id="页面-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g id="首页" transform="translate(-428.000000, -73.000000)" fill="#FFFFFF"><g id="编组备份-4" transform="translate(88.000000, 58.000000)"><g id="xiala" transform="translate(340.000000, 15.500000)"><g transform="translate(0.875000, 1.227565)"><path d="M3.46545138,-0.937321433 C3.70372007,-0.94067733 3.89959549,-0.750242892 3.90295138,-0.511974211 C3.90300844,-0.507923486 3.90300844,-0.503872158 3.90295138,-0.499821433 L3.79045138,7.48767857 C3.78697149,7.73475093 3.58752375,7.93419867 3.34045138,7.93767857 C3.1021827,7.94103446 2.90630728,7.75060003 2.90295138,7.51233134 C2.90289433,7.50828062 2.90289433,7.50422929 2.90295138,7.50017857 L3.01545138,-0.487321433 C3.01893128,-0.734393797 3.21837902,-0.933841541 3.46545138,-0.937321433 Z" id="矩形" transform="translate(3.402951, 3.500179) rotate(-45.000000) translate(-3.402951, -3.500179) "></path><path d="M8.90891729,-0.937321433 C9.15598965,-0.933841541 9.35543739,-0.734393797 9.35891729,-0.487321433 L9.47141729,7.50017857 C9.47477318,7.73844725 9.28433875,7.93432267 9.04607006,7.93767857 C9.04201934,7.93773562 9.03796801,7.93773562 9.03391729,7.93767857 C8.78684492,7.93419867 8.58739718,7.73475093 8.58391729,7.48767857 L8.47141729,-0.499821433 C8.46806139,-0.738090114 8.65849583,-0.933965536 8.89676451,-0.937321433 C8.90081523,-0.937378486 8.90486656,-0.937378486 8.90891729,-0.937321433 Z" id="矩形备份-2" transform="translate(8.971417, 3.500179) rotate(-315.000000) translate(-8.971417, -3.500179) "></path></g></g></g></g></g></svg></div>
        <div>${(allToolsButton.genelistName as string).includes('metabon') ? i18n.metabon : (allToolsButton.genelistName as string).includes('protein') ? i18n.protein : i18n.gene }</div>
      </div>
      <div>${(allToolsButton.genelistName as string).includes('metabon') ? i18n.intensity : i18n.midCount}</div>
      `);
      const content = makeElement('div',['ng-details-of-more-gene-info-content']);
      let total:number = 0;
      for(let key in response.data){
        content.innerHTML += `<div><span>${key}</span><span>${thousandth_formatter(response.data[key])}</span></div>`;
        total += Number(response.data[key]);
      }
      const bottom = makeElement('div',['ng-details-of-more-gene-info-bottom'],{},`<span>${(allToolsButton.genelistName as string).includes('metabon') ? i18n.total_intensity : i18n.total_mid_count}</span><span>${thousandth_formatter(total)}</span>`)
      element.appendChild(title);
      element.appendChild(content);
      element.appendChild(bottom);
      title.addEventListener('click',()=>{
        flag = !flag;
        if(flag){
          // 折叠
          title.classList.remove('details-of-more-gene-fold')
          content.classList.add('fold');
          content.classList.remove('expand');
        }else{
          // 展开
          title.classList.add('details-of-more-gene-fold')
          content.classList.remove('fold');
          content.classList.add('expand');
        }
      })
    }else{
      new message(`${i18n.asyncfail} ${response.msg}`,'error')
    }
  }

  abstract translateDataPointByViewportPixels(
      out: vec3, orig: vec3, deltaX: number, deltaY: number): vec3;

  onMouseout() {
    this.updateMousePosition(-1, -1);
    this.viewer.mouseState.setForcer(undefined);
  }

  abstract translateByViewportPixels(deltaX: number, deltaY: number): void;

  handleMouseMove(clientX: number, clientY: number) {
    const {element} = this;
    const bounds = element.getBoundingClientRect();
    const mouseX = clientX - (bounds.left + element.clientLeft);
    const mouseY = clientY - (bounds.top + element.clientTop);
    const {mouseState} = this.viewer;
    mouseState.pageX = clientX + window.scrollX;
    mouseState.pageY = clientY + window.scrollY;
    mouseState.setForcer(this.mouseStateForcer);
    this.updateMousePosition(mouseX, mouseY);
  }

  onMousemove(event: MouseEvent, atOnly:boolean = true) {
    const {element} = this;
    if (atOnly && event.target !== element) {
      return;
    }
    this.handleMouseMove(event.clientX, event.clientY);
  }

  onTouchstart(event: TouchEvent) {
    let {element} = this;
    if (event.target !== element || event.targetTouches.length !== 1) {
      return;
    }
    const {clientX, clientY} = event.targetTouches[0];
    this.handleMouseMove(clientX, clientY);
  }

  disposed() {
    let {mouseState} = this.viewer;
    mouseState.removeForcer(this.mouseStateForcer);
    const {gl} = this;
    this.cancelPickRequests();
    const {pendingPickRequestTimerId} = this;
    if (pendingPickRequestTimerId !== -1) {
      window.clearTimeout(pendingPickRequestTimerId);
    }
    for (const request of this.pickRequests) {
      gl.deleteBuffer(request.buffer);
    }
    super.disposed();
  }

  abstract zoomByMouse(factor: number): void;
}
